import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "flex justify-center w-full font-sourcesanspro-semibold text-l2" }
const _hoisted_2 = { class: "px-[34px] pb-[120px] w-full max-w-[600px] mx-auto" }
const _hoisted_3 = { class: "font-sourcesanspro-regular text-white/70 text-sm2 mb-4" }
const _hoisted_4 = {
  key: 0,
  class: "mb-4 flex flex-wrap gap-2"
}
const _hoisted_5 = ["onClick"]
const _hoisted_6 = { class: "rounded backdrop-blur-xl bg-white/10 p-3" }
const _hoisted_7 = {
  key: 1,
  class: "mt-2 text-xs2 text-red-200 font-sourcesanspro-regular"
}
const _hoisted_8 = {
  key: 2,
  class: "mt-4 rounded backdrop-blur-xl bg-white/10 p-4 flex items-center gap-3"
}
const _hoisted_9 = { class: "flex-1" }
const _hoisted_10 = { class: "font-sourcesanspro-semibold text-base2 text-white" }
const _hoisted_11 = { class: "text-white/60 text-xs2 font-sourcesanspro-regular" }
const _hoisted_12 = ["disabled"]

import { computed, ref, watch } from 'vue';
import { IonContent, IonFooter, IonTextarea, IonToggle } from '@ionic/vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import NavigationBar from '@/shared/components/ui/NavigationBar.vue';
import TopBar from '@/shared/components/TopBar.vue';
import useSafety, { IncidentType } from '@/modules/safety/composables/useSafety';
import useGeolocation from '@/modules/safety/composables/useGeolocation';
import useToast from '@/shared/composables/useToast';
import BackButton from '@/shared/components/BackButton.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'SafetyReportFormPage',
  setup(__props) {

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const { reportIncident, getSafetyConfig, isReporting } = useSafety();
const { config } = getSafetyConfig();
const { getCurrentPosition, isSupported: geoSupported } = useGeolocation();
const { presentToast } = useToast();

const type = (route.params.type as IncidentType) ?? 'general';
const description = ref('');
const shareLocation = ref(true);
const selectedQuickMessage = ref<string | null>(null);
const manualLocationReason = ref<'missing' | 'disabled' | null>(null);
const isPreparingReport = ref(false);
const isSubmitting = computed(() => isPreparingReport.value || isReporting.value);
const manualLocationHint = computed(() => {
	if (manualLocationReason.value === 'missing') {
		return t('safety.location_missing_comment_hint');
	}

	if (manualLocationReason.value === 'disabled') {
		return t('safety.location_disabled_comment_hint');
	}

	return '';
});
const descriptionPlaceholder = computed(() => (manualLocationReason.value ? t('safety.describe_location_placeholder') : t('safety.describe_placeholder')));

const quickMessages = computed<string[]>(() => {
	const list = config.value?.quick_messages?.[type];
	const messages = Array.isArray(list) ? list : typeof list === 'string' ? [list] : [];

	return messages
		.flatMap((message) => message.split(/\r\n|\r|\n/))
		.map((message) => message.trim())
		.filter(Boolean);
});

const pickQuickMessage = (msg: string) => {
	const previousQuickMessage = selectedQuickMessage.value;
	const currentDescription = description.value.trim();

	if (previousQuickMessage === msg) {
		selectedQuickMessage.value = null;
		if (currentDescription === msg) {
			description.value = '';
		}
		return;
	}

	selectedQuickMessage.value = msg;
	if (!currentDescription || currentDescription === previousQuickMessage) {
		description.value = msg;
	}
};

watch(shareLocation, (enabled) => {
	if (config.value?.gps_required) {
		return;
	}

	if (enabled) {
		manualLocationReason.value = null;
		return;
	}

	if (manualLocationReason.value !== 'missing') {
		manualLocationReason.value = 'disabled';
	}
});

const submit = async () => {
	if (isSubmitting.value) {
		return;
	}

	if (config.value?.enabled === false) {
		await presentToast('top', t('safety.disabled'), 5000, 'danger');
		router.replace({ name: 'dashboard' });
		return;
	}

	const requiresLocation = config.value?.gps_required === true;
	const shouldRequestLocation = shareLocation.value || requiresLocation;
	const currentDescription = description.value.trim();

	if (!requiresLocation && !shareLocation.value && !currentDescription) {
		manualLocationReason.value = 'disabled';
		return;
	}

	isPreparingReport.value = shouldRequestLocation;
	const coords = shouldRequestLocation ? await getCurrentPosition(4000).finally(() => (isPreparingReport.value = false)) : null;

	if (requiresLocation && !coords) {
		await presentToast('top', t('safety.location_required'), 5000, 'danger');
		return;
	}

	if (shareLocation.value && !coords) {
		manualLocationReason.value = 'missing';
		shareLocation.value = false;
		return;
	}

	const incident = await reportIncident({
		type,
		description: description.value.trim() || selectedQuickMessage.value || undefined,
		latitude: coords?.latitude,
		longitude: coords?.longitude,
		gps_accuracy: coords?.accuracy,
	});
	if (incident) {
		router.replace({ name: 'safety-my-report-detail', params: { id: incident.id } });
	}
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(IonContent), null, {
      default: _withCtx(() => [
        _createVNode(TopBar, { class: "px-[34px]" }, {
          left: _withCtx(() => [
            _createVNode(BackButton, { to: "/safety" })
          ]),
          center: _withCtx(() => [
            _createElementVNode("div", _hoisted_1, _toDisplayString(_unref(t)(`safety.types.${_unref(type)}`)), 1)
          ]),
          _: 1
        }),
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("p", _hoisted_3, _toDisplayString(_unref(t)(`safety.type_hint.${_unref(type)}`)), 1),
          (quickMessages.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(quickMessages.value, (msg) => {
                  return (_openBlock(), _createElementBlock("button", {
                    key: msg,
                    class: _normalizeClass(["rounded-full px-3 py-1.5 text-sm2 font-sourcesanspro-semibold border", selectedQuickMessage.value === msg ? 'bg-white text-black border-white' : 'text-white border-white/40']),
                    onClick: ($event: any) => (pickQuickMessage(msg))
                  }, _toDisplayString(msg), 11, _hoisted_5))
                }), 128))
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_6, [
            _createVNode(_unref(IonTextarea), {
              modelValue: description.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((description).value = $event)),
              placeholder: descriptionPlaceholder.value,
              "auto-grow": true,
              rows: 4,
              class: "text-white font-sourcesanspro-regular"
            }, null, 8, ["modelValue", "placeholder"])
          ]),
          (manualLocationHint.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_7, _toDisplayString(manualLocationHint.value), 1))
            : _createCommentVNode("", true),
          (_unref(geoSupported))
            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                _createElementVNode("div", _hoisted_9, [
                  _createElementVNode("div", _hoisted_10, _toDisplayString(_unref(t)('safety.share_location')), 1),
                  _createElementVNode("div", _hoisted_11, _toDisplayString(_unref(t)('safety.share_location_hint')), 1)
                ]),
                _createVNode(_unref(IonToggle), {
                  modelValue: shareLocation.value,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((shareLocation).value = $event)),
                  disabled: _unref(config)?.gps_required
                }, null, 8, ["modelValue", "disabled"])
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("button", {
            disabled: isSubmitting.value,
            class: "mt-6 w-full h-[60px] rounded-full bg-red-600 text-white font-sourcesanspro-semibold text-base2 uppercase active:scale-[0.98] transition-transform disabled:opacity-70",
            onClick: submit
          }, _toDisplayString(isSubmitting.value ? _unref(t)('safety.reporting') : _unref(t)('safety.send_report')), 9, _hoisted_12)
        ])
      ]),
      _: 1
    }),
    _createVNode(_unref(IonFooter), null, {
      default: _withCtx(() => [
        _createVNode(NavigationBar, { "item-active": "safety" })
      ]),
      _: 1
    })
  ], 64))
}
}

})